import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { Draggable } from '@fullcalendar/interaction';
import api from '../services/api';
import { useUser } from '../contexts/UserContext';

function Calendar() {
  const { canCreateData } = useUser();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleJob, setScheduleJob] = useState(null);
  const [saving, setSaving] = useState(false);

  // Schedule form state
  const [scheduleMode, setScheduleMode] = useState('time'); // 'time' or 'multiday'
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleEndDate, setScheduleEndDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [durationHours, setDurationHours] = useState(2);
  const [durationMinutes, setDurationMinutes] = useState(0);

  const calendarRef = useRef(null);
  const unscheduledRef = useRef(null);

  useEffect(() => {
    fetchJobs();
  }, []);

  // Set up draggable for unscheduled jobs (quick drop defaults to time mode)
  useEffect(() => {
    if (unscheduledRef.current && !loading) {
      const draggable = new Draggable(unscheduledRef.current, {
        itemSelector: '.draggable-job',
        eventData: (eventEl) => {
          const jobId = eventEl.getAttribute('data-job-id');
          const jobTitle = eventEl.getAttribute('data-job-title');
          return {
            id: jobId,
            title: jobTitle,
            duration: { hours: 2 },
            backgroundColor: '#3b82f6',
            borderColor: '#3b82f6',
          };
        },
      });

      return () => draggable.destroy();
    }
  }, [loading, jobs]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const jobsData = await api.getJobs();
      setJobs(jobsData);
    } catch (err) {
      setError(err.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  // Convert jobs to FullCalendar events
  const events = jobs
    .filter((job) => job.scheduled_date)
    .map((job) => {
      const isMultiDay = job.scheduled_end_date && job.scheduled_end_date !== job.scheduled_date;

      // Determine color based on status
      let backgroundColor = '#3b82f6'; // blue - default/active
      if (job.quote_accepted_at) {
        backgroundColor = '#22c55e'; // green - accepted
      } else if (job.quote_sent_at) {
        backgroundColor = '#f59e0b'; // amber - quote sent
      }

      if (isMultiDay) {
        // Multi-day event (all-day spanning multiple days)
        // FullCalendar needs end date to be exclusive, so add 1 day
        const endDate = new Date(job.scheduled_end_date);
        endDate.setDate(endDate.getDate() + 1);

        return {
          id: job.id,
          title: job.title,
          start: job.scheduled_date,
          end: endDate.toISOString().split('T')[0],
          allDay: true,
          backgroundColor,
          borderColor: backgroundColor,
          extendedProps: {
            job,
            isMultiDay: true,
            clientName: job.client_name,
            address: job.address,
          },
        };
      } else {
        // Single day with time
        const startTime = job.scheduled_time || '09:00';
        const duration = parseFloat(job.duration_hours) || 2;
        const start = new Date(`${job.scheduled_date}T${startTime}`);
        const end = new Date(start);
        end.setHours(end.getHours() + Math.floor(duration));
        end.setMinutes(end.getMinutes() + (duration % 1) * 60);

        return {
          id: job.id,
          title: job.title,
          start,
          end,
          allDay: false,
          backgroundColor,
          borderColor: backgroundColor,
          extendedProps: {
            job,
            isMultiDay: false,
            clientName: job.client_name,
            address: job.address,
          },
        };
      }
    });

  // Open schedule modal for a job
  const openScheduleModal = (job, defaultDate = null) => {
    setScheduleJob(job);

    // Determine current mode from job data
    const isMultiDay = job.scheduled_end_date && job.scheduled_end_date !== job.scheduled_date;
    setScheduleMode(isMultiDay ? 'multiday' : 'time');

    // Set form values from job or defaults
    setScheduleDate(job.scheduled_date || defaultDate || new Date().toISOString().split('T')[0]);
    setScheduleEndDate(job.scheduled_end_date || job.scheduled_date || defaultDate || new Date().toISOString().split('T')[0]);
    setScheduleTime(job.scheduled_time || '09:00');

    // Parse duration into hours and minutes
    const totalHours = parseFloat(job.duration_hours) || 2;
    setDurationHours(Math.floor(totalHours));
    setDurationMinutes(Math.round((totalHours % 1) * 60));

    setShowScheduleModal(true);
  };

  // Save schedule
  const saveSchedule = async () => {
    if (!scheduleJob) return;
    if (!canCreateData) return; // Guard: trial expired

    setSaving(true);
    try {
      const updateData = {
        scheduled_date: scheduleDate,
      };

      if (scheduleMode === 'multiday') {
        updateData.scheduled_end_date = scheduleEndDate;
        updateData.scheduled_time = null;
        updateData.duration_hours = null;
      } else {
        updateData.scheduled_end_date = null;
        updateData.scheduled_time = scheduleTime;
        updateData.duration_hours = durationHours + (durationMinutes / 60);
      }

      await api.updateJob(scheduleJob.id, updateData);

      // Update local state
      setJobs((prevJobs) =>
        prevJobs.map((job) =>
          job.id === scheduleJob.id
            ? { ...job, ...updateData }
            : job
        )
      );

      setShowScheduleModal(false);
      setScheduleJob(null);
    } catch (err) {
      alert('Failed to save schedule: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Clear schedule (unschedule a job)
  const clearSchedule = async () => {
    if (!scheduleJob) return;
    if (!canCreateData) return; // Guard: trial expired

    if (!window.confirm('Remove this job from the calendar?')) return;

    setSaving(true);
    try {
      await api.updateJob(scheduleJob.id, {
        scheduled_date: null,
        scheduled_end_date: null,
        scheduled_time: null,
        duration_hours: null,
      });

      // Update local state
      setJobs((prevJobs) =>
        prevJobs.map((job) =>
          job.id === scheduleJob.id
            ? { ...job, scheduled_date: null, scheduled_end_date: null, scheduled_time: null, duration_hours: null }
            : job
        )
      );

      setShowScheduleModal(false);
      setScheduleJob(null);
    } catch (err) {
      alert('Failed to clear schedule: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle event click (show schedule modal for editing)
  const handleEventClick = (info) => {
    const job = info.event.extendedProps.job;
    openScheduleModal(job);
  };

  // Handle drag & drop to reschedule (quick reschedule keeps same mode)
  const handleEventDrop = useCallback(async (info) => {
    const jobId = info.event.id;
    const job = jobs.find(j => j.id === jobId);
    const isMultiDay = job?.scheduled_end_date && job?.scheduled_end_date !== job?.scheduled_date;

    const newDate = info.event.start.toISOString().split('T')[0];

    try {
      const updateData = { scheduled_date: newDate };

      if (isMultiDay && info.event.end) {
        // Calculate new end date (FullCalendar end is exclusive)
        const endDate = new Date(info.event.end);
        endDate.setDate(endDate.getDate() - 1);
        updateData.scheduled_end_date = endDate.toISOString().split('T')[0];
      } else if (!isMultiDay) {
        updateData.scheduled_time = info.event.start.toTimeString().slice(0, 5);
      }

      await api.updateJob(jobId, updateData);

      setJobs((prevJobs) =>
        prevJobs.map((j) =>
          j.id === jobId ? { ...j, ...updateData } : j
        )
      );
    } catch (err) {
      info.revert();
      alert('Failed to update schedule: ' + err.message);
    }
  }, [jobs]);

  // Handle resize to change duration
  const handleEventResize = useCallback(async (info) => {
    const jobId = info.event.id;
    const job = jobs.find(j => j.id === jobId);
    const isMultiDay = job?.scheduled_end_date && job?.scheduled_end_date !== job?.scheduled_date;

    try {
      if (isMultiDay) {
        // Calculate new end date
        const endDate = new Date(info.event.end);
        endDate.setDate(endDate.getDate() - 1);
        await api.updateJob(jobId, {
          scheduled_end_date: endDate.toISOString().split('T')[0],
        });

        setJobs((prevJobs) =>
          prevJobs.map((j) =>
            j.id === jobId
              ? { ...j, scheduled_end_date: endDate.toISOString().split('T')[0] }
              : j
          )
        );
      } else {
        // Calculate duration in hours
        const start = info.event.start;
        const end = info.event.end;
        const durationMs = end - start;
        const durationHours = Math.round((durationMs / (1000 * 60 * 60)) * 2) / 2;

        await api.updateJob(jobId, { duration_hours: durationHours });

        setJobs((prevJobs) =>
          prevJobs.map((j) =>
            j.id === jobId ? { ...j, duration_hours: durationHours } : j
          )
        );
      }
    } catch (err) {
      info.revert();
      alert('Failed to update: ' + err.message);
    }
  }, [jobs]);

  // Handle external job dropped onto calendar (quick schedule as time slot)
  const handleEventReceive = useCallback(async (info) => {
    const jobId = info.event.id;
    const newDate = info.event.start.toISOString().split('T')[0];
    const newTime = info.event.start.toTimeString().slice(0, 5);

    try {
      await api.updateJob(jobId, {
        scheduled_date: newDate,
        scheduled_time: newTime,
        duration_hours: 2,
        scheduled_end_date: null,
      });

      setJobs((prevJobs) =>
        prevJobs.map((job) =>
          job.id === jobId
            ? { ...job, scheduled_date: newDate, scheduled_time: newTime, duration_hours: 2, scheduled_end_date: null }
            : job
        )
      );

      info.event.remove();
    } catch (err) {
      info.revert();
      alert('Failed to schedule job: ' + err.message);
    }
  }, []);

  // Format time for display
  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="py-20 px-6 bg-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-slate-600">Loading calendar...</p>
        </div>
      </div>
    );
  }

  const unscheduledJobs = jobs.filter((j) => !j.scheduled_date);

  return (
    <div className="py-8 px-6 bg-background min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-black text-primary mb-2">Calendar</h1>
            <p className="text-slate-600">Drag jobs to reschedule, click to edit</p>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/jobs"
              className="inline-flex items-center space-x-2 bg-slate-100 text-slate-700 px-4 py-2 rounded-xl font-medium hover:bg-slate-200 transition"
            >
              <i className="fas fa-list"></i>
              <span>List View</span>
            </Link>
          </div>
        </div>

        {/* Legend */}
        <div className="mb-4 flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-blue-500"></div>
            <span className="text-slate-600">Active</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-amber-500"></div>
            <span className="text-slate-600">Quote Sent</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-500"></div>
            <span className="text-slate-600">Accepted</span>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
            {error}
          </div>
        )}

        {/* Calendar */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay',
            }}
            events={events}
            eventClick={handleEventClick}
            eventDrop={handleEventDrop}
            eventResize={handleEventResize}
            eventReceive={handleEventReceive}
            editable={canCreateData}
            droppable={canCreateData}
            selectable={canCreateData}
            selectMirror={true}
            dayMaxEvents={true}
            weekends={true}
            nowIndicator={true}
            height="auto"
            eventTimeFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short',
            }}
            slotMinTime="06:00:00"
            slotMaxTime="22:00:00"
          />
        </div>

        {/* Unscheduled Jobs */}
        {unscheduledJobs.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-primary mb-2">Unscheduled Jobs</h2>
            <p className="text-sm text-slate-500 mb-4">
              <i className="fas fa-info-circle mr-1"></i>
              Click to schedule with options, or drag onto calendar for quick time slot
            </p>
            <div ref={unscheduledRef} className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {unscheduledJobs.map((job) => (
                <div
                  key={job.id}
                  className="draggable-job bg-white p-4 rounded-xl shadow-sm border border-slate-200 hover:shadow-md hover:border-secondary transition cursor-pointer"
                  data-job-id={job.id}
                  data-job-title={job.title}
                  onClick={() => openScheduleModal(job)}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-slate-300 mt-1 cursor-grab" title="Drag to calendar">
                      <i className="fas fa-grip-vertical"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      {job.job_number && (
                        <span className="text-xs font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded mb-1 inline-block">
                          #{job.job_number.replace('JOB-', '')}
                        </span>
                      )}
                      <h3 className="font-bold text-primary truncate">{job.title}</h3>
                      {job.client_name && (
                        <p className="text-sm text-slate-600 truncate">
                          <i className="fas fa-user mr-1 text-slate-400"></i>
                          {job.client_name}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openScheduleModal(job);
                      }}
                      className="text-secondary hover:text-orange-600 p-1"
                      title="Schedule job"
                    >
                      <i className="fas fa-calendar-plus"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Schedule Modal */}
        {showScheduleModal && scheduleJob && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-primary">Schedule Job</h2>
                    <p className="text-slate-600">{scheduleJob.title}</p>
                  </div>
                  <button
                    onClick={() => setShowScheduleModal(false)}
                    className="text-slate-400 hover:text-slate-600 text-xl"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>

                {/* Schedule Mode Toggle */}
                <div className="flex gap-2 mb-6">
                  <button
                    onClick={() => setScheduleMode('time')}
                    className={`flex-1 py-3 px-4 rounded-xl font-medium transition ${
                      scheduleMode === 'time'
                        ? 'bg-primary text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    <i className="fas fa-clock mr-2"></i>
                    Time Slot
                  </button>
                  <button
                    onClick={() => setScheduleMode('multiday')}
                    className={`flex-1 py-3 px-4 rounded-xl font-medium transition ${
                      scheduleMode === 'multiday'
                        ? 'bg-primary text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    <i className="fas fa-calendar-week mr-2"></i>
                    Multi-Day
                  </button>
                </div>

                {scheduleMode === 'time' ? (
                  /* Time Slot Mode */
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Date</label>
                      <input
                        type="date"
                        value={scheduleDate}
                        onChange={(e) => setScheduleDate(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">Start Time</label>
                        <input
                          type="time"
                          value={scheduleTime}
                          onChange={(e) => setScheduleTime(e.target.value)}
                          className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">Duration</label>
                        <div className="flex gap-2">
                          <select
                            value={durationHours}
                            onChange={(e) => setDurationHours(parseInt(e.target.value, 10))}
                            className="flex-1 px-3 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                          >
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((h) => (
                              <option key={h} value={h}>{h}h</option>
                            ))}
                          </select>
                          <select
                            value={durationMinutes}
                            onChange={(e) => setDurationMinutes(parseInt(e.target.value, 10))}
                            className="flex-1 px-3 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                          >
                            <option value={0}>00m</option>
                            <option value={15}>15m</option>
                            <option value={30}>30m</option>
                            <option value={45}>45m</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-slate-500">
                      <i className="fas fa-info-circle mr-1"></i>
                      {scheduleDate && `${formatDate(scheduleDate)} at ${formatTime(scheduleTime)} (${durationHours}h ${durationMinutes > 0 ? durationMinutes + 'm' : ''})`}
                    </p>
                  </div>
                ) : (
                  /* Multi-Day Mode */
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Start Date</label>
                      <input
                        type="date"
                        value={scheduleDate}
                        onChange={(e) => {
                          setScheduleDate(e.target.value);
                          if (e.target.value > scheduleEndDate) {
                            setScheduleEndDate(e.target.value);
                          }
                        }}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">End Date</label>
                      <input
                        type="date"
                        value={scheduleEndDate}
                        min={scheduleDate}
                        onChange={(e) => setScheduleEndDate(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                      />
                    </div>
                    <p className="text-sm text-slate-500">
                      <i className="fas fa-info-circle mr-1"></i>
                      {scheduleDate && scheduleEndDate && (
                        scheduleDate === scheduleEndDate
                          ? `All day on ${formatDate(scheduleDate)}`
                          : `${formatDate(scheduleDate)} to ${formatDate(scheduleEndDate)}`
                      )}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-6 pt-4 border-t border-slate-200">
                  {!canCreateData && (
                    <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                      <i className="fas fa-lock mr-2"></i>
                      Upgrade to Pro to modify schedules
                    </div>
                  )}
                  <div className="flex gap-3">
                    <button
                      onClick={saveSchedule}
                      disabled={saving || !scheduleDate || !canCreateData}
                      className={`flex-1 px-4 py-3 rounded-xl font-medium transition ${
                        canCreateData
                          ? 'bg-secondary text-white hover:bg-orange-600 disabled:opacity-50'
                          : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                      }`}
                    >
                      {saving ? (
                        <><i className="fas fa-spinner fa-spin mr-2"></i>Saving...</>
                      ) : (
                        <><i className={`fas ${canCreateData ? 'fa-check' : 'fa-lock'} mr-2`}></i>Save Schedule</>
                      )}
                    </button>
                    {scheduleJob.scheduled_date && canCreateData && (
                      <button
                        onClick={clearSchedule}
                        disabled={saving}
                        className="px-4 py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-red-100 hover:text-red-700 transition disabled:opacity-50"
                        title="Remove from calendar"
                      >
                        <i className="fas fa-calendar-times"></i>
                      </button>
                    )}
                  </div>
                </div>

                {/* View Job Link */}
                <div className="mt-4 text-center">
                  <Link
                    to={`/jobs/${scheduleJob.id}`}
                    className="text-primary hover:underline text-sm"
                  >
                    <i className="fas fa-external-link-alt mr-1"></i>
                    View Job Details
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Calendar;
